import { apiFetch } from './client.ts';
import type {
  GalaxyMapResponse, SystemDetailResponse, MeResponse,
  FleetListResponse, FleetClient, TravelCommand,
  CivilizationResources, BuildOutpostCommand, BuildStationCommand, ConstructionClient,
  CivilizationPublic, DiplomacyEntry, DiplomacyStatus,
  BattleReport, AttackCommand, ConflictReport, SystemAttackCommand, BattleReplay,
  BuildShipCommand, ShipOrderClient,
  CreateFleetCommand, SplitFleetCommand, MergeFleetCommand, TransferShipsCommand, FleetMutationResponse,
  FleetCommandsResponse, EnqueueFleetCommand, EconomyOverview,
  BattleStateResponse, BattleCommandType, BattleDoctrine, DiplomacyOverview, ProposalType, TreatyType, ResearchState, ScientistsState, DiscoveryClient, RepairInfo, RepairClient,
  NpcConversation, NpcTalkRequest, NpcTalkResponse,
  RoutePreview, BulkFleetCommand, BulkFleetResponse, FleetGroupClient, FleetGroupMembersCommand,
} from '@galaxis/shared';

export async function fetchMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>('/me');
}

// M10.2: account/auth calls live in ./account.ts

export async function fetchGalaxyMap(): Promise<GalaxyMapResponse> {
  return apiFetch<GalaxyMapResponse>('/galaxy/map');
}

export async function fetchSystem(id: string): Promise<SystemDetailResponse> {
  return apiFetch<SystemDetailResponse>(`/systems/${id}`);
}

export async function fetchFleets(): Promise<FleetListResponse> {
  return apiFetch<FleetListResponse>('/fleets');
}

// ---- Fleet management (M6/7) ----

export async function createFleet(command: CreateFleetCommand): Promise<FleetMutationResponse> {
  return apiFetch('/fleets', { method: 'POST', body: JSON.stringify(command) });
}

export async function renameFleet(fleetId: string, name: string): Promise<FleetMutationResponse> {
  return apiFetch(`/fleets/${fleetId}`, { method: 'PATCH', body: JSON.stringify({ name }) });
}

export async function splitFleet(fleetId: string, command: SplitFleetCommand): Promise<FleetMutationResponse> {
  return apiFetch(`/fleets/${fleetId}/split`, { method: 'POST', body: JSON.stringify(command) });
}

export async function mergeFleets(targetFleetId: string, command: MergeFleetCommand): Promise<FleetMutationResponse> {
  return apiFetch(`/fleets/${targetFleetId}/merge`, { method: 'POST', body: JSON.stringify(command) });
}

export async function transferShips(targetFleetId: string, command: TransferShipsCommand): Promise<FleetMutationResponse> {
  return apiFetch(`/fleets/${targetFleetId}/ships`, { method: 'POST', body: JSON.stringify(command) });
}

export async function disbandFleet(fleetId: string): Promise<FleetMutationResponse> {
  return apiFetch(`/fleets/${fleetId}`, { method: 'DELETE' });
}

// ---- Javítás (M10.11) ----

export async function fetchRepairInfo(fleetId: string): Promise<RepairInfo> {
  return apiFetch(`/fleets/${fleetId}/repair`);
}

export async function startRepair(fleetId: string, idempotencyKey: string): Promise<{ repair: RepairClient }> {
  return apiFetch(`/fleets/${fleetId}/repair`, { method: 'POST', body: JSON.stringify({ idempotencyKey }) });
}

export async function surveyWithFleet(fleetId: string, idempotencyKey: string): Promise<{ fleet: FleetClient; completesAt: string }> {
  return apiFetch(`/fleets/${fleetId}/survey`, { method: 'POST', body: JSON.stringify({ idempotencyKey }) });
}

// ---- Research (M9) ----

export async function fetchResearch(): Promise<ResearchState> {
  return apiFetch('/research');
}

export async function startResearch(techId: string): Promise<{ status: 'started' | 'queued'; completesAt: string | null }> {
  return apiFetch('/research', { method: 'POST', body: JSON.stringify({ techId, idempotencyKey: `research-${techId}-${Date.now()}` }) });
}

export async function cancelResearch(techId: string): Promise<{ refunded: { metal: number; energy: number; rp: number } }> {
  return apiFetch(`/research/${encodeURIComponent(techId)}`, { method: 'DELETE' });
}

export async function buildLab(systemId: string, area?: 'physics' | 'society' | 'engineering'): Promise<{ completesAt: string }> {
  return apiFetch('/research/labs', { method: 'POST', body: JSON.stringify({ systemId, ...(area ? { area } : {}), idempotencyKey: `lab-${systemId}-${Date.now()}` }) });
}

export async function upgradeLab(labId: string): Promise<{ completesAt: string }> {
  return apiFetch('/research/labs/upgrade', { method: 'POST', body: JSON.stringify({ labId, idempotencyKey: `lab-up-${labId}-${Date.now()}` }) });
}

export async function fetchScientists(): Promise<ScientistsState> {
  return apiFetch('/research/scientists');
}
export async function hireScientist(offerSeq: number, slot: number): Promise<{ scientistId: string }> {
  return apiFetch('/research/scientists/hire', { method: 'POST', body: JSON.stringify({ offerSeq, slot, idempotencyKey: `sci-${offerSeq}-${slot}-${Date.now()}` }) });
}
export async function assignScientist(scientistId: string, area: 'physics' | 'society' | 'engineering' | null): Promise<void> {
  await apiFetch('/research/scientists/assign', { method: 'POST', body: JSON.stringify({ scientistId, area }) });
}
export async function dismissScientist(scientistId: string): Promise<void> {
  await apiFetch(`/research/scientists/${encodeURIComponent(scientistId)}`, { method: 'DELETE' });
}

export async function fetchDiscoveries(): Promise<DiscoveryClient[]> {
  return apiFetch('/research/discoveries');
}

// ---- Diplomacy, treaties, trade (M8) ----

export async function fetchDiplomacyOverview(): Promise<DiplomacyOverview> {
  return apiFetch('/diplomacy/overview');
}

const dkey = (p: string) => `${p}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export async function proposeTreaty(targetCivId: string, type: ProposalType): Promise<void> {
  await apiFetch('/diplomacy/proposals', { method: 'POST', body: JSON.stringify({ targetCivId, type, idempotencyKey: dkey(type) }) });
}

export async function answerProposal(id: string, action: 'accept' | 'reject' | 'cancel'): Promise<void> {
  await apiFetch(`/diplomacy/proposals/${id}/${action}`, { method: 'POST' });
}

export async function cancelTreatyWith(targetCivId: string, type: TreatyType): Promise<{ endsAt: string }> {
  return apiFetch('/diplomacy/treaties/cancel', { method: 'POST', body: JSON.stringify({ targetCivId, type }) });
}

export async function sendResources(targetCivId: string, metal: number, energy: number): Promise<void> {
  await apiFetch('/diplomacy/transfers', { method: 'POST', body: JSON.stringify({ targetCivId, metal, energy, idempotencyKey: dkey('send') }) });
}

// ---- NPC leader conversation (Track 5) ----

export type NpcTalkBody = NpcTalkRequest extends infer T ? (T extends unknown ? Omit<T, 'idempotencyKey'> : never) : never;

export async function fetchNpcConversation(npcCivId: string): Promise<NpcConversation> {
  return apiFetch(`/diplomacy/npc/${npcCivId}/conversation`);
}

export async function talkToNpc(npcCivId: string, body: NpcTalkBody): Promise<NpcTalkResponse> {
  return apiFetch(`/diplomacy/npc/${npcCivId}/talk`, { method: 'POST', body: JSON.stringify({ ...body, idempotencyKey: dkey('talk') }) });
}

// ---- Tactical battle control (M6/11) ----

export async function fetchBattleState(battleId: string): Promise<BattleStateResponse> {
  return apiFetch(`/battles/${battleId}/state`);
}

export async function sendBattleCommand(
  battleId: string,
  cmd: { fleetId: string; type: BattleCommandType; targetShipId?: string; targetX?: number; targetY?: number; doctrine?: BattleDoctrine },
): Promise<void> {
  await apiFetch(`/battles/${battleId}/commands`, {
    method: 'POST',
    body: JSON.stringify({ ...cmd, idempotencyKey: `${cmd.type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` }),
  });
}

// ---- Economy (M6/9–M6/10) ----

export async function fetchEconomyOverview(): Promise<EconomyOverview> {
  return apiFetch('/economy/overview');
}

export async function acknowledgeEconomy(): Promise<void> {
  await apiFetch('/economy/acknowledge', { method: 'POST' });
}

export async function upgradeStation(stationId: string): Promise<{ ok: true; toLevel: number; completesAt: string }> {
  return apiFetch(`/stations/${stationId}/upgrade`, { method: 'POST' });
}

// ---- Fleet command queue (M6/8) ----

export async function fetchFleetCommands(fleetId: string): Promise<FleetCommandsResponse> {
  return apiFetch(`/fleets/${fleetId}/commands`);
}

export async function enqueueFleetCommand(fleetId: string, command: EnqueueFleetCommand): Promise<FleetCommandsResponse> {
  return apiFetch(`/fleets/${fleetId}/commands`, { method: 'POST', body: JSON.stringify(command) });
}

export async function cancelFleetCommand(fleetId: string, commandId: string): Promise<FleetCommandsResponse> {
  return apiFetch(`/fleets/${fleetId}/commands/${commandId}`, { method: 'DELETE' });
}

export async function reorderFleetCommands(fleetId: string, commandIds: string[]): Promise<FleetCommandsResponse> {
  return apiFetch(`/fleets/${fleetId}/commands/reorder`, { method: 'POST', body: JSON.stringify({ commandIds }) });
}

export async function stopFleetCommands(fleetId: string): Promise<FleetCommandsResponse> {
  return apiFetch(`/fleets/${fleetId}/commands/stop`, { method: 'POST' });
}

export async function issueTravel(
  fleetId: string,
  command: TravelCommand,
): Promise<{ fleet: FleetClient; arrivesAt: string }> {
  return apiFetch<{ fleet: FleetClient; arrivesAt: string }>(`/fleets/${fleetId}/travel`, {
    method: 'POST',
    body: JSON.stringify(command),
  });
}

export async function fetchResources(): Promise<CivilizationResources> {
  return apiFetch<CivilizationResources>('/economy/resources');
}

export async function buildOutpost(
  systemId: string,
  command: BuildOutpostCommand,
): Promise<{ construction: ConstructionClient; completesAt: string }> {
  return apiFetch(`/systems/${systemId}/outpost`, {
    method: 'POST',
    body: JSON.stringify(command),
  });
}

export async function buildStation(
  systemId: string,
  command: BuildStationCommand,
): Promise<{ construction: ConstructionClient; completesAt: string }> {
  return apiFetch(`/systems/${systemId}/stations`, {
    method: 'POST',
    body: JSON.stringify(command),
  });
}

export async function fetchConstructions(systemId: string): Promise<{ constructions: ConstructionClient[] }> {
  return apiFetch(`/systems/${systemId}/constructions`);
}

export async function fetchCivilizations(): Promise<CivilizationPublic[]> {
  return apiFetch<CivilizationPublic[]>('/civilizations');
}

export async function fetchDiplomacy(): Promise<DiplomacyEntry[]> {
  return apiFetch<DiplomacyEntry[]>('/diplomacy');
}

export async function setDiplomacy(civId: string, status: DiplomacyStatus): Promise<DiplomacyEntry> {
  return apiFetch<DiplomacyEntry>(`/diplomacy/${civId}`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

export async function fetchFleetsAtSystem(systemId: string): Promise<{
  id: string; civilizationId: string; isOwn: boolean; status: string;
  ships: { id: string; name: string; class: string }[];
}[]> {
  return apiFetch(`/systems/${systemId}/fleets`);
}

export async function issueAttack(command: AttackCommand): Promise<{ id: string; status: 'in_progress' }> {
  return apiFetch('/battles/attack', {
    method: 'POST',
    body: JSON.stringify(command),
  });
}

export async function fetchBattle(battleId: string): Promise<BattleReport> {
  return apiFetch<BattleReport>(`/battles/${battleId}`);
}

export async function fetchBattles(): Promise<BattleReport[]> {
  return apiFetch<BattleReport[]>('/battles');
}

export async function issueSystemAttack(
  systemId: string,
  command: SystemAttackCommand,
): Promise<{ conflict: ConflictReport; battleId: string | null }> {
  return apiFetch(`/systems/${systemId}/attack`, {
    method: 'POST',
    body: JSON.stringify(command),
  });
}

export async function fetchSystemConflict(systemId: string): Promise<ConflictReport | null> {
  try {
    return await apiFetch<ConflictReport>(`/systems/${systemId}/conflict`);
  } catch {
    return null;
  }
}

export async function fetchConflicts(): Promise<ConflictReport[]> {
  return apiFetch<ConflictReport[]>('/conflicts');
}

export async function retreatFleet(fleetId: string): Promise<void> {
  await apiFetch(`/fleets/${fleetId}/retreat`, { method: 'POST' });
}

export async function fetchBattleReplay(battleId: string): Promise<BattleReplay | null> {
  try {
    return await apiFetch<BattleReplay>(`/battles/${battleId}/replay`);
  } catch {
    return null;
  }
}

export async function buildShip(
  systemId: string,
  command: BuildShipCommand,
): Promise<{ order: ShipOrderClient }> {
  return apiFetch(`/systems/${systemId}/ships`, {
    method: 'POST',
    body: JSON.stringify(command),
  });
}

export async function fetchShipOrders(systemId: string): Promise<ShipOrderClient[]> {
  try {
    const res = await apiFetch<{ orders: ShipOrderClient[] }>(`/systems/${systemId}/ship-orders`);
    return res.orders;
  } catch {
    return [];
  }
}

// ---- Track 2: shareable battle report ----

/** Creates (or returns) the public link token of a completed battle. */
export async function shareBattleReport(battleId: string): Promise<{ token: string; path: string }> {
  return apiFetch(`/battles/${battleId}/share`, { method: 'POST' });
}

export async function unshareBattleReport(battleId: string): Promise<void> {
  await apiFetch(`/battles/${battleId}/share`, { method: 'DELETE' });
}

/** Public, no login: the sanitised replay behind a share link (null when unknown / revoked). */
export async function fetchSharedBattle(token: string): Promise<import('@galaxis/shared').PublicBattleReplay | null> {
  try {
    return await apiFetch(`/public/battles/${encodeURIComponent(token)}`, { auth: false });
  } catch {
    return null;
  }
}

// ---- Fleet management extras: route preview, groups, bulk commands ----

export async function previewFleetRoute(fleetId: string, targetSystemId: string): Promise<RoutePreview> {
  return apiFetch(`/fleets/${fleetId}/route-preview`, { method: 'POST', body: JSON.stringify({ targetSystemId }) });
}

export async function bulkFleetCommand(command: BulkFleetCommand): Promise<BulkFleetResponse> {
  return apiFetch('/fleet-commands/bulk', { method: 'POST', body: JSON.stringify(command) });
}

interface GroupsResponse { groups: FleetGroupClient[]; group: FleetGroupClient | null }

export async function fetchFleetGroups(): Promise<GroupsResponse> {
  return apiFetch('/fleet-groups');
}

export async function createFleetGroup(name: string, idempotencyKey: string, color?: string): Promise<GroupsResponse> {
  return apiFetch('/fleet-groups', { method: 'POST', body: JSON.stringify({ name, idempotencyKey, ...(color ? { color } : {}) }) });
}

export async function updateFleetGroup(groupId: string, patch: { name?: string; color?: string }): Promise<GroupsResponse> {
  return apiFetch(`/fleet-groups/${groupId}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteFleetGroup(groupId: string): Promise<GroupsResponse> {
  return apiFetch(`/fleet-groups/${groupId}`, { method: 'DELETE' });
}

export async function setFleetGroupMembers(groupId: string, command: FleetGroupMembersCommand): Promise<GroupsResponse> {
  return apiFetch(`/fleet-groups/${groupId}/members`, { method: 'POST', body: JSON.stringify(command) });
}
